import React, { useMemo, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AgentAvatar from "../AgentAvatar";
import CommonStatusPill from "../../common/StatusPill";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import {
  FormSurface,
  StatusPill,
  useFormPalette,
} from "../forms/DynamicFormPrimitives";
import {
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

export function canRenderArtifactToolCard(result) {
  const parsed = parseMaybeJson(result);
  return Boolean(parsed && !parsed.error && parsed.artifactInstanceId);
}

function eventLabels(events) {
  if (!Array.isArray(events)) return [];
  return events
    .map((event) => event?.name)
    .filter((name) => typeof name === "string" && name.trim());
}

export function artifactStatusTone(status) {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "WAITING") return "warning";
  if (normalized === "COMPLETED" || normalized === "DONE") return "success";
  if (normalized === "ACTIVE" || normalized === "IN_PROGRESS") return "waiting";
  if (normalized === "ERROR" || normalized === "FAILED" || normalized === "CANCELLED") {
    return "error";
  }
  return "neutral";
}

export default function ArtifactToolCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings = null,
}) {
  const palette = useFormPalette();
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const [copied, setCopied] = useState(false);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const title = parsedArgs?.title || parsedResult.title || "Artifact";
  const artifactInstanceId = parsedResult.artifactInstanceId;
  const shareUrl = parsedResult.shareUrl;
  const status = parsedResult.status || "WAITING";
  const events = eventLabels(parsedResult.detectedEvents || parsedArgs?.allowedEvents);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
        return;
      }
    } catch (_error) {
      // Fall through to manual-copy prompt below.
    }
    if (typeof window.prompt === "function") {
      window.prompt("Copy artifact link", shareUrl);
    }
  };

  const eventTagStyle = {
    fontFamily: "monospace",
    fontSize: "10px",
    fontWeight: 600,
  };

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar
            agentId={agentId}
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
            sx={getClickableAgentAvatarSx(avatarClick)}
          />
        )}
        <Box sx={{ width: "75%", minWidth: "280px" }}>
          <FormSurface
            title={title}
            rightSlot={
              <StatusPill
                label={status}
                tone={artifactStatusTone(status)}
                formatLabel={false}
              />
            }
          >
            {artifactInstanceId && (
              <Typography sx={{ fontSize: "12px", color: palette.textSecondary, mb: 0.75 }}>
                Artifact ID:{" "}
                <Box
                  component="span"
                  sx={{ fontFamily: "monospace", color: palette.textPrimary }}
                >
                  {artifactInstanceId}
                </Box>
              </Typography>
            )}
            {shareUrl && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: palette.textSecondary,
                  mb: events.length > 0 ? 0.75 : 0,
                  wordBreak: "break-all",
                }}
              >
                Share URL:{" "}
                <Box component="span" sx={{ color: palette.textPrimary }}>
                  {shareUrl}
                </Box>
              </Typography>
            )}
            {events.length > 0 && (
              <>
                <Typography sx={{ fontSize: "12px", color: palette.textSecondary }}>
                  Expected events
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75, mb: 0.5 }}>
                  {events.map((eventName) => (
                    <CommonStatusPill
                      key={eventName}
                      variant="neutral"
                      size="sm"
                      style={eventTagStyle}
                    >
                      {eventName}
                    </CommonStatusPill>
                  ))}
                </Box>
              </>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Tooltip title={copied ? "Copied" : "Copy link"}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!shareUrl}
                    onClick={handleCopy}
                    startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: "none", fontSize: "12px" }}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                disabled={!shareUrl}
                onClick={() => window.open(shareUrl, "_blank", "noopener")}
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontSize: "12px" }}
              >
                Open
              </Button>
            </Box>
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
